/**
 * InvoiceReview — review and confirm an extracted invoice before saving.
 *
 * Shows:
 *   - Editable line items (description, kind, quantity, amount in major units).
 *   - Detected document_type with manual override dropdown.
 *   - Vendor name and order/invoice date (editable).
 *
 * On confirm:
 *   - document_type === "shop_invoice" → writes a ServiceEntry to Firestore
 *     vehicles/{vehicleId}/service (uses useAddServiceEntry pattern).
 *   - document_type === "parts_order" | "receipt" | "unknown" → does NOT write
 *     a ServiceEntry. Shows a confirmation that the doc is saved.
 *
 * COST FENCING: cost/totals are editable here but MUST NOT be rendered on the
 * timeline/service-log list. This component only renders them pre-confirm.
 */

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import { useAddServiceEntry } from '@/hooks/useAddServiceEntry';
import type { InvoiceExtractionResult, LineItem, LineItemKind } from '@/types/domain';
import { format } from 'date-fns';
import type { CSSProperties } from 'react';
import { useState } from 'react';

interface InvoiceReviewProps {
  vehicleId: string;
  authorUid: string;
  /** SHA-256 of the uploaded document. Stored as invoiceImageRef on ServiceEntry. */
  imageRef: string;
  result: InvoiceExtractionResult;
  onDone: () => void;
  onRetry: () => void;
}

type DocumentType = 'shop_invoice' | 'parts_order' | 'receipt' | 'unknown';

interface EditableLineItem {
  description: string;
  kind: LineItemKind | '';
  partNumber: string;
  quantity: string;
  /** Amount in MAJOR units (dollars) as a string for input editing. */
  amountMajor: string;
  currency: string;
}

/** Convert minor units (cents) to major units string (dollars). */
function minorToMajor(minor: number | undefined): string {
  if (minor === undefined || minor === null) return '';
  return (minor / 100).toFixed(2);
}

/** Parse major units string back to minor units (cents). Returns 0 on invalid. */
function majorToMinor(major: string): number {
  const n = parseFloat(major);
  if (isNaN(n)) return 0;
  return Math.round(n * 100);
}

function mapRawLineItems(raw: InvoiceExtractionResult['line_items']): EditableLineItem[] {
  return raw.map((item) => ({
    description: item.description,
    kind: (item.kind as LineItemKind) ?? '',
    partNumber: item.part_number ?? '',
    quantity: item.quantity?.toString() ?? '',
    amountMajor: minorToMajor(item.amount_minor),
    currency: item.currency ?? '',
  }));
}

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  shop_invoice: 'Shop invoice',
  parts_order: 'Parts order',
  receipt: 'Receipt',
  unknown: 'Unknown',
};

const LINE_ITEM_KIND_OPTIONS: Array<{ value: LineItemKind | ''; label: string }> = [
  { value: '', label: '— select —' },
  { value: 'part', label: 'Part' },
  { value: 'labor', label: 'Labor' },
  { value: 'fee', label: 'Fee' },
  { value: 'tax', label: 'Tax' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'discount', label: 'Discount' },
  { value: 'other', label: 'Other' },
];

export function InvoiceReview({
  vehicleId,
  authorUid: _authorUid,
  imageRef,
  result,
  onDone,
  onRetry,
}: InvoiceReviewProps) {
  const [docType, setDocType] = useState<DocumentType>(result.document_type as DocumentType);
  const [vendor, setVendor] = useState(result.vendor ?? '');
  const [orderDate, setOrderDate] = useState(result.order_date ?? '');
  const [lineItems, setLineItems] = useState<EditableLineItem[]>(
    mapRawLineItems(result.line_items),
  );
  const [confirmed, setConfirmed] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { add, pending } = useAddServiceEntry();

  function updateItem(index: number, field: keyof EditableLineItem, value: string) {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  }

  function removeItem(index: number) {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  }

  function addItem() {
    setLineItems((prev) => [
      ...prev,
      { description: '', kind: '', partNumber: '', quantity: '', amountMajor: '', currency: '' },
    ]);
  }

  async function handleConfirm() {
    setSubmitError(null);

    if (docType === 'shop_invoice') {
      // Write a ServiceEntry to Firestore
      const mappedLineItems: LineItem[] = lineItems.map((item) => ({
        description: item.description,
        ...(item.kind ? { kind: item.kind as LineItemKind } : {}),
        ...(item.partNumber ? { partNumber: item.partNumber } : {}),
        ...(item.quantity ? { quantity: parseFloat(item.quantity) } : {}),
        amountMinor: majorToMinor(item.amountMajor),
        ...(item.currency ? { currency: item.currency } : {}),
      }));

      const totalMinor = result.total_minor;
      const currency = result.currency ?? lineItems.find((i) => i.currency)?.currency;
      const n = lineItems.length;
      const summary = vendor
        ? `${vendor} · ${n} item${n !== 1 ? 's' : ''}`
        : `Invoice · ${n} item${n !== 1 ? 's' : ''}`;
      const performedAt = orderDate || format(new Date(), 'yyyy-MM-dd');

      try {
        await add({
          vehicleId,
          entry: {
            summary,
            performedAt,
            source: 'shop_invoice',
            ...(vendor ? { vendor } : {}),
            invoiceImageRef: imageRef,
            lineItems: mappedLineItems,
            ...(totalMinor !== undefined ? { totalMinor } : {}),
            ...(currency ? { currency } : {}),
          },
        });
        setConfirmed(true);
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Could not save entry');
      }
    } else {
      // Parts order / receipt / unknown — document is already uploaded (parked).
      // No ServiceEntry written.
      setConfirmed(true);
    }
  }

  const wrapStyle: CSSProperties = { display: 'grid', gap: SPACE.md, fontFamily: F.body };

  const inputStyle: CSSProperties = {
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    color: T.text,
    padding: `${SPACE.sm}px ${SPACE.md}px`,
    fontSize: 14,
    fontFamily: F.body,
    width: '100%',
    boxSizing: 'border-box',
  };

  const selectStyle: CSSProperties = { ...inputStyle };
  const labelStyle: CSSProperties = { fontSize: 12, color: T.textMuted, letterSpacing: '0.04em' };

  // -----------------------------------------------------------------------
  // Confirmed state
  // -----------------------------------------------------------------------

  if (confirmed) {
    const isInvoice = docType === 'shop_invoice';
    return (
      <div style={wrapStyle}>
        <div
          style={{
            background: T.card,
            border: `1px solid ${T.border}`,
            borderLeft: `3px solid ${T.green}`,
            borderRadius: RADIUS.md,
            padding: SPACE.md,
          }}
        >
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: T.text }}>
            {isInvoice ? 'Saved to service log' : 'Document saved'}
          </p>
          <p
            style={{
              margin: `${SPACE.xs}px 0 0`,
              fontSize: 13,
              color: T.textMuted,
              lineHeight: 1.5,
            }}
          >
            {isInvoice
              ? 'The invoice has been added to your service log.'
              : "Saved as a parts order with this vehicle's documents. It was not added to the service log."}
          </p>
        </div>
        <Button variant="secondary" onClick={onDone}>
          Done
        </Button>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Review form
  // -----------------------------------------------------------------------

  return (
    <div style={wrapStyle}>
      <div
        style={{
          background: T.card,
          border: `1px solid ${T.border}`,
          borderLeft: `3px solid ${T.accent}`,
          borderRadius: RADIUS.md,
          padding: SPACE.md,
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: T.textMuted,
          }}
        >
          Review before saving
        </p>
        <p
          style={{ margin: `${SPACE.xs}px 0 0`, fontSize: 13, color: T.textMuted, lineHeight: 1.5 }}
        >
          Check the extracted details below. All fields are editable.
        </p>
      </div>

      {/* Document type selector */}
      <Card>
        <label style={{ display: 'grid', gap: SPACE.sm }}>
          <span style={labelStyle}>Document type</span>
          <select
            style={selectStyle}
            value={docType}
            onChange={(e) => setDocType(e.target.value as DocumentType)}
          >
            {(Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[]).map((key) => (
              <option key={key} value={key}>
                {DOCUMENT_TYPE_LABELS[key]}
              </option>
            ))}
          </select>
        </label>
      </Card>

      {/* Vendor + date */}
      <Card>
        <div style={{ display: 'grid', gap: SPACE.md }}>
          <label style={{ display: 'grid', gap: SPACE.sm }}>
            <span style={labelStyle}>Vendor / shop</span>
            <input
              type="text"
              style={inputStyle}
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="e.g. AutoZone, Bob's Garage"
            />
          </label>
          <label style={{ display: 'grid', gap: SPACE.sm }}>
            <span style={labelStyle}>Date</span>
            <input
              type="date"
              style={inputStyle}
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
            />
          </label>
        </div>
      </Card>

      {/* Line items */}
      <div>
        <p style={{ margin: `0 0 ${SPACE.sm}px`, fontSize: 13, fontWeight: 600, color: T.text }}>
          Line items
        </p>
        <div style={{ display: 'grid', gap: SPACE.sm }}>
          {lineItems.map((item, idx) => (
            <Card key={idx} padding="md">
              <div style={{ display: 'grid', gap: SPACE.sm }}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{ fontSize: 12, color: T.textMuted }}>Item {idx + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: T.textMuted,
                      cursor: 'pointer',
                      fontSize: 12,
                      padding: `${SPACE.xs}px`,
                    }}
                    aria-label={`Remove item ${idx + 1}`}
                  >
                    Remove
                  </button>
                </div>

                <label style={{ display: 'grid', gap: SPACE.sm }}>
                  <span style={labelStyle}>Description</span>
                  <input
                    type="text"
                    style={inputStyle}
                    value={item.description}
                    onChange={(e) => updateItem(idx, 'description', e.target.value)}
                    placeholder="e.g. Front brake pads"
                  />
                </label>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.sm }}>
                  <label style={{ display: 'grid', gap: SPACE.sm }}>
                    <span style={labelStyle}>Kind</span>
                    <select
                      style={selectStyle}
                      value={item.kind}
                      onChange={(e) => updateItem(idx, 'kind', e.target.value)}
                    >
                      {LINE_ITEM_KIND_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={{ display: 'grid', gap: SPACE.sm }}>
                    <span style={labelStyle}>Qty</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="any"
                      style={inputStyle}
                      value={item.quantity}
                      onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                    />
                  </label>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.sm }}>
                  <label style={{ display: 'grid', gap: SPACE.sm }}>
                    <span style={labelStyle}>Amount ($)</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      style={inputStyle}
                      value={item.amountMajor}
                      onChange={(e) => updateItem(idx, 'amountMajor', e.target.value)}
                      placeholder="0.00"
                    />
                  </label>

                  <label style={{ display: 'grid', gap: SPACE.sm }}>
                    <span style={labelStyle}>Currency</span>
                    <input
                      type="text"
                      style={inputStyle}
                      value={item.currency}
                      onChange={(e) => updateItem(idx, 'currency', e.target.value)}
                      placeholder="USD"
                      maxLength={3}
                    />
                  </label>
                </div>

                {item.partNumber !== undefined ? (
                  <label style={{ display: 'grid', gap: SPACE.sm }}>
                    <span style={labelStyle}>Part number</span>
                    <input
                      type="text"
                      style={inputStyle}
                      value={item.partNumber}
                      onChange={(e) => updateItem(idx, 'partNumber', e.target.value)}
                    />
                  </label>
                ) : null}
              </div>
            </Card>
          ))}
        </div>

        <button
          type="button"
          onClick={addItem}
          style={{
            marginTop: SPACE.sm,
            background: 'none',
            border: `1px dashed ${T.border}`,
            borderRadius: RADIUS.md,
            color: T.textMuted,
            cursor: 'pointer',
            fontSize: 13,
            padding: `${SPACE.sm}px ${SPACE.md}px`,
            width: '100%',
          }}
        >
          + Add line item
        </button>
      </div>

      {submitError ? (
        <p role="alert" style={{ color: T.red, fontSize: 13, margin: 0 }}>
          {submitError}
        </p>
      ) : null}

      <Button
        fullWidth
        loading={pending}
        onClick={() => {
          void handleConfirm();
        }}
      >
        {docType === 'shop_invoice' ? 'Save to service log' : 'Save document'}
      </Button>

      <Button variant="secondary" fullWidth onClick={onRetry}>
        Upload a different document
      </Button>
    </div>
  );
}
